import { LegalLayout, Section } from "./LegalLayout";

export default function Privacy() {
  return (
    <LegalLayout title="Privacy Policy" updated="June 2026" testid="privacy-page">
      <p>
        OperaMind ("OperaMind", "we", "us" or "our") provides an AI receptionist and communications
        platform that answers calls, books appointments, handles enquiries and supports businesses. This
        Privacy Policy explains what personal data we collect, how we use it, who we share it with, and the
        rights available to you under the UK GDPR and the Data Protection Act 2018.
      </p>
      <p>
        This policy covers our website, dashboard and services (together, the "Service"). If you have any
        questions, contact us at <a href="mailto:admin@operamind.uk" className="text-blue-600 hover:underline">admin@operamind.uk</a>.
      </p>

      <Section heading="1. Who is the data controller?">
        <p>
          For personal data relating to our customers (business account holders), OperaMind is the data
          controller. When our customers use OperaMind to handle calls and messages from their own
          customers, OperaMind acts as a data processor on that customer's behalf, and the customer is the
          controller of that end-caller data.
        </p>
      </Section>

      <Section heading="2. Information we collect">
        <p>We collect the following categories of information:</p>
        <ul className="list-disc pl-6 space-y-1.5">
          <li><strong>Account data</strong> — name, business name, email address, phone number, and login credentials.</li>
          <li><strong>Business & verification data</strong> — company details, registration numbers, addresses and, for phone-number regulatory verification, identity or proof-of-address documents required by our telephony provider.</li>
          <li><strong>Call & message data</strong> — call recordings, transcripts, summaries, voicemails, appointments and messages processed by your AI receptionist.</li>
          <li><strong>Payment data</strong> — billing details processed securely by our payment provider (we do not store full card numbers).</li>
          <li><strong>Usage & technical data</strong> — IP address, device and browser information, log data and cookies.</li>
        </ul>
      </Section>

      <Section heading="3. How we use your information">
        <ul className="list-disc pl-6 space-y-1.5">
          <li>To provide, operate and improve the Service, including provisioning phone numbers and AI assistants.</li>
          <li>To process calls, transcribe and summarise conversations, and book appointments on your behalf.</li>
          <li>To verify your identity and satisfy telephony regulatory requirements.</li>
          <li>To take payments and manage your subscription.</li>
          <li>To provide customer support and send service-related communications.</li>
          <li>To detect, prevent and address fraud, abuse and security issues.</li>
          <li>To comply with our legal obligations.</li>
        </ul>
      </Section>

      <Section heading="4. Legal bases for processing">
        <p>
          We rely on: performance of a contract (to deliver the Service you sign up for); legitimate
          interests (to secure and improve the Service); consent (where required, e.g. certain cookies or
          marketing); and legal obligation (e.g. tax and regulatory record-keeping).
        </p>
      </Section>

      <Section heading="5. Third-party services we use">
        <p>We share data with trusted providers strictly to deliver the Service:</p>
        <ul className="list-disc pl-6 space-y-1.5">
          <li><strong>Twilio</strong> — telephony, phone numbers and regulatory verification.</li>
          <li><strong>Vapi</strong> — real-time voice AI for handling calls.</li>
          <li><strong>OpenAI / AI model providers</strong> — transcription, summarisation and AI responses.</li>
          <li><strong>Stripe</strong> — payment processing and subscription billing.</li>
          <li><strong>Email & calendar providers</strong> — notifications and booking integrations you choose to connect.</li>
        </ul>
        <p>
          These providers process data under their own terms and appropriate safeguards. We do not sell your
          personal data.
        </p>
      </Section>

      <Section heading="6. Call recordings & AI processing">
        <p>
          Calls handled by your AI receptionist may be recorded and transcribed to provide summaries,
          bookings and message logs. As the business using OperaMind, you are responsible for informing your
          callers that calls may be recorded and for having a lawful basis to do so.
        </p>
      </Section>

      <Section heading="7. International transfers">
        <p>
          Some of our providers may process data outside the UK/EEA. Where this happens, we rely on
          appropriate safeguards such as the UK International Data Transfer Agreement or Standard Contractual
          Clauses.
        </p>
      </Section>

      <Section heading="8. Data retention">
        <p>
          We keep personal data only for as long as necessary to provide the Service and to meet legal,
          accounting or reporting obligations. Call data and transcripts are retained for the duration of
          your account unless you delete them or request earlier removal.
        </p>
      </Section>

      <Section heading="9. Your rights">
        <p>Under UK data protection law you have the right to:</p>
        <ul className="list-disc pl-6 space-y-1.5">
          <li>Access a copy of your personal data.</li>
          <li>Correct inaccurate or incomplete data.</li>
          <li>Request erasure of your data.</li>
          <li>Restrict or object to certain processing.</li>
          <li>Data portability.</li>
          <li>Withdraw consent at any time (where processing is based on consent).</li>
        </ul>
        <p>
          To exercise any of these rights, email <a href="mailto:admin@operamind.uk" className="text-blue-600 hover:underline">admin@operamind.uk</a>.
          You also have the right to lodge a complaint with the UK Information Commissioner's Office (ICO).
        </p>
      </Section>

      <Section heading="10. Security">
        <p>
          We use industry-standard technical and organisational measures to protect your data, including
          encryption in transit, access controls and secure infrastructure. No method of transmission or
          storage is completely secure, but we work hard to protect your information.
        </p>
      </Section>

      <Section heading="11. Cookies">
        <p>
          We use essential cookies to keep you signed in and to operate the Service, and may use analytics
          cookies to understand usage. You can control cookies through your browser settings.
        </p>
      </Section>

      <Section heading="12. Changes to this policy">
        <p>
          We may update this Privacy Policy from time to time. Material changes will be notified via the
          Service or by email. The "Last updated" date above reflects the latest revision.
        </p>
      </Section>

      <Section heading="13. Contact us">
        <p>
          Questions about this policy or your data? Email us at{" "}
          <a href="mailto:admin@operamind.uk" className="text-blue-600 hover:underline">admin@operamind.uk</a>.
        </p>
      </Section>
    </LegalLayout>
  );
}
